const VENUE_TIMEZONE = process.env.REACT_APP_TIMEZONE || 'Pacific/Honolulu';

function parseDateInput(date: string | Date): Date | null {
    if (!date) return null;

    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTimeslotInTimezone(
    date: string | Date,
    locale: string,
    options: Intl.DateTimeFormatOptions,
): string {
    const parsedDate = parseDateInput(date);
    if (!parsedDate) return '';

    return parsedDate.toLocaleString(locale, {
        ...options,
        timeZone: VENUE_TIMEZONE,
    });
}

function getPartsInTz(date: Date, tz: string): { year: number; month: number; day: number } {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return { year: Number(v.year), month: Number(v.month), day: Number(v.day) };
}

/**
 * Returns date key YYYY-MM-DD in venue timezone. Use for grouping/matching calendar days.
 */
export function getDateKeyInTimezone(dateTime: string | Date): string {
    const date = parseDateInput(dateTime);
    if (!date) return '';
    const p = getPartsInTz(date, VENUE_TIMEZONE);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * Check if timeslot falls on the same calendar day as the given Date, in venue timezone.
 */
export function isSameDayInVenueTimezone(
    timeslotStartTime: string | Date,
    calendarDayDate: Date,
): boolean {
    const tsKey = getDateKeyInTimezone(timeslotStartTime);
    const pad = (n: number) => String(n).padStart(2, '0');
    const dayKey = `${calendarDayDate.getFullYear()}-${pad(calendarDayDate.getMonth() + 1)}-${pad(calendarDayDate.getDate())}`;
    return tsKey === dayKey;
}

/**
 * Format a number to a string
 * @description Format a number to a string with commas and two decimal places
 * @param num number
 * @param locale string
 * @returns string
 */
export function formatNumber(num: number, locale: string = 'en-US'): string {
    // Check if the number is NaN or not a number
    if (isNaN(num) || typeof num !== 'number') {
        return '';
    }
    // Check if the number is an integer
    if (Number.isInteger(num)) {
        return new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }).format(num);
    }
    // Format the number with commas and two decimal places
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(num);
}

export function formatCurrency(
    num: number,
    locale: string = 'en-US',
    currency: string = 'USD',
): string {
    // Check if the number is NaN or not a number
    if (isNaN(num) || typeof num !== 'number') {
        return '';
    }
    // Format the number with commas and two decimal places
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(num);
}

/**
 * Format a date to a string
 * @description Format a date to a string with the format 'MM/DD/YYYY HH:mm' (localized)
 * @param date string | Date
 * @param locale string
 * @returns string
 */
export function formatDate(date: string | Date, locale: string = 'en-US'): string {
    if (!date) return '';
    return formatTimeslotInTimezone(date, locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
    });
}

/**
 * Format a date to a string with weekday
 * @description Format a date to a string with the format 'MM/DD/YYYY HH:mm, Weekday' (localized)
 * @param date string | Date
 * @param locale string
 * @returns string
 */
export function formatDateWithWeekDay(date: string | Date, locale: string = 'en-US'): string {
    if (!date) return '';
    return formatTimeslotInTimezone(date, locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        weekday: 'long',
    });
}
